import {
  interpretarProximaVisita,
  type ProximaVisita,
} from '@sheafyield/dominio';
import { useId } from 'react';

import { Campo } from '../ds/Campo.js';

/**
 * O que o texto digitado quer dizer, ou `null` quando não quer dizer nada.
 *
 * Existe como função exportada, e não como cálculo escondido no componente,
 * porque **quem grava e quem mostra têm de ser a mesma conta**: a folha chama
 * isto com o mesmo `texto` e o mesmo `agora` que o campo usa para desenhar a
 * pré-visualização, então o que se grava é, por construção, o que a
 * pré-visualização diz. Duas chamadas da mesma função pura com as mesmas
 * entradas não têm como divergir.
 *
 * Texto em branco é `null` sem passar pelo domínio: em branco não é engano de
 * digitação, é ausência de pedido, e não merece um "não entendi" na tela.
 */
export function lerProximaVisita(
  texto: string,
  agora: Date | null,
): ProximaVisita | null {
  if (agora === null || texto.trim() === '') return null;
  return interpretarProximaVisita(texto, agora);
}

export interface PropsCampoProximaVisita {
  texto: string;
  /** O instante contra o qual "sábado 10h" é resolvido. Fixado quando a folha
   *  abre os detalhes, e não recalculado a cada tecla: se ele andasse, o que
   *  a pessoa leu na pré-visualização e o que o toque no chip grava seriam
   *  contas diferentes. `null` enquanto os detalhes nunca foram abertos. */
  agora: Date | null;
  aoMudar: (texto: string) => void;
}

/**
 * "Próxima visita" em linguagem natural, com pré-visualização (spec §8).
 *
 * Digitar "sábado 10h" mostra "sábado, 14 de março, às 10h" logo abaixo do
 * campo, e é **isso** que se grava. Texto que o domínio não entende não vira
 * data nenhuma: a pré-visualização diz que não entendeu e a folha grava vazio
 * — agendar a visita errada é pior do que não agendar, porque a pessoa fica
 * esperando na porta.
 *
 * A pré-visualização é uma região viva (`role="status"`) **e** a descrição do
 * campo. Viva porque ela muda enquanto se digita e ninguém que usa VoiceOver
 * vai sair do campo para conferir; descrição porque quem chega ao campo
 * depois precisa ouvir o que já está marcado. Ela nasce montada e vazia: uma
 * região viva que entra no documento já com texto não é anunciada.
 */
export function CampoProximaVisita({
  texto,
  agora,
  aoMudar,
}: PropsCampoProximaVisita) {
  const gerado = useId();
  const idPrevia = `proxima${gerado}-previa`;
  const entendida = lerProximaVisita(texto, agora);
  const emBranco = texto.trim() === '';
  const estado = emBranco ? 'vazio' : entendida === null ? 'nao' : 'sim';
  return (
    <div className="registrar__proxima">
      <Campo
        rotulo="Próxima visita"
        placeholder="sábado 10h, em 2 semanas, 15/03"
        value={texto}
        onChange={(evento) => {
          aoMudar(evento.target.value);
        }}
        // Vence o `aria-describedby` que o `Campo` monta sozinho: aqui quem
        // descreve o campo é a pré-visualização, e não há dica nem erro.
        aria-describedby={idPrevia}
        autoComplete="off"
        enterKeyHint="done"
      />
      <p
        className="registrar__previa"
        id={idPrevia}
        role="status"
        data-entendida={estado}
      >
        {estado === 'vazio' ? null : entendida === null ? (
          <>
            {/* O "não entendi" não vai só na cor: o símbolo é o que sobra em
                monocromia e sob contraste alto. */}
            <span aria-hidden="true">⚠</span> Não entendi. Nada será agendado.
          </>
        ) : (
          entendida.descricao
        )}
      </p>
    </div>
  );
}
